import React, { useState } from "react";
import { KeyRound, ShieldAlert } from "lucide-react";
import { portalApi } from "../../lib/portalApi";
import { usePortalAuth } from "../../contexts/PortalAuthContext";

export default function ForcePasswordChangeModal() {
  const { refresh, logout } = usePortalAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (next.length < 8) { setErr("New password must be at least 8 characters."); return; }
    if (next === current) { setErr("New password must be different from the temporary password."); return; }
    if (next !== confirm) { setErr("New password and confirmation do not match."); return; }
    setBusy(true);
    try {
      await portalApi.changePassword(current, next);
      await refresh(); // re-fetches user; force_password_change is now false → modal unmounts
    } catch (e) {
      setErr(e.message || "Could not update password.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="portal-force-pw-modal"
      className="fixed inset-0 z-[100] bg-[#1C110C]/85 backdrop-blur-md flex items-center justify-center px-4"
    >
      <form
        onSubmit={submit}
        data-testid="portal-force-pw-form"
        className="bg-white w-full max-w-[480px] border border-[#6E1F2C]/25 p-8 md:p-10"
      >
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#FAF5EC] mb-4">
            <ShieldAlert className="text-[#6E1F2C]" size={22} />
          </div>
          <p className="text-[10px] tracking-[0.32em] uppercase text-[#6E1F2C] mb-2">Action required</p>
          <h2 className="font-serif-display text-2xl md:text-3xl text-[#1C110C] leading-tight">
            Create Your New Password
          </h2>
          <p className="text-sm text-[#3E2A22] mt-3 leading-relaxed">
            For security, please create a new password before accessing your project dashboard.
          </p>
        </div>

        <label className="block text-[10px] tracking-[0.3em] uppercase text-[#7A6B5F] mb-2">Current temporary password</label>
        <input
          type="password"
          data-testid="portal-force-pw-current"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          required
          autoComplete="current-password"
          className="w-full bg-[#FAF5EC] border border-[#6E1F2C]/20 px-4 py-3 mb-4 text-[#1C110C] focus:outline-none focus:border-[#6E1F2C]"
        />

        <label className="block text-[10px] tracking-[0.3em] uppercase text-[#7A6B5F] mb-2">New password (min 8 chars)</label>
        <input
          type="password"
          data-testid="portal-force-pw-new"
          value={next}
          onChange={(e) => setNext(e.target.value)}
          required
          minLength={8}
          autoComplete="new-password"
          className="w-full bg-[#FAF5EC] border border-[#6E1F2C]/20 px-4 py-3 mb-4 text-[#1C110C] focus:outline-none focus:border-[#6E1F2C]"
        />

        <label className="block text-[10px] tracking-[0.3em] uppercase text-[#7A6B5F] mb-2">Confirm new password</label>
        <input
          type="password"
          data-testid="portal-force-pw-confirm"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          required
          minLength={8}
          autoComplete="new-password"
          className="w-full bg-[#FAF5EC] border border-[#6E1F2C]/20 px-4 py-3 mb-5 text-[#1C110C] focus:outline-none focus:border-[#6E1F2C]"
        />

        {err && (
          <p data-testid="portal-force-pw-error" className="text-xs text-[#A3141A] mb-4">{err}</p>
        )}

        <button
          type="submit"
          disabled={busy}
          data-testid="portal-force-pw-submit"
          className="w-full inline-flex items-center justify-center gap-2 bg-[#6E1F2C] text-[#FAF5EC] px-6 py-3.5 text-[11px] tracking-[0.28em] uppercase hover:bg-[#56151F] transition-colors disabled:opacity-60"
        >
          <KeyRound size={14} /> {busy ? "Updating…" : "Set new password & continue"}
        </button>

        <button
          type="button"
          onClick={logout}
          data-testid="portal-force-pw-logout"
          className="w-full mt-4 text-[10px] tracking-[0.3em] uppercase text-[#7A6B5F] hover:text-[#6E1F2C]"
        >
          Sign out instead
        </button>
      </form>
    </div>
  );
}
